import React from 'react';
import { Clock, PlayCircle, RefreshCcw } from 'lucide-react';
import { format } from 'date-fns';
import { SubPageLayout } from '@/components/layout/SubPageLayout';
import { useMyPurchases } from '@/features/wallet/hooks/useMyPurchases';
import { formatCurrency } from '@/lib/pricing';
import type { MyPurchaseItem } from '@/features/wallet/hooks/useMyPurchases';
import type { RollReviewStatus } from '@/types/wallet';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isRefundedStatus(status: RollReviewStatus): boolean {
  return status === 'declined' || status === 'cancelled' || status === 'expired' || status === 'refunded';
}

function hoursUntil(isoDate: string | null): number | null {
  if (!isoDate) return null;
  const diff = new Date(isoDate).getTime() - Date.now();
  if (diff <= 0) return 0;
  return Math.floor(diff / 3600000);
}

// ─── Status label ─────────────────────────────────────────────────────────────

function StatusLabel({ item }: { item: MyPurchaseItem }) {
  if (isRefundedStatus(item.status)) {
    return (
      <span className="text-[10px] font-bold tracking-wider" style={{ color: '#E74C3C' }}>
        REFUNDED
      </span>
    );
  }

  if (item.status === 'delivered') {
    return (
      <span className="text-[10px] font-bold tracking-wider" style={{ color: '#2ECC71' }}>
        DELIVERED
      </span>
    );
  }

  // Accepted reviews carry a persisted delivery deadline (SOS-358); show
  // the countdown until expiry so the practitioner can see time remaining.
  if (item.status === 'in_review') {
    const hours = hoursUntil(item.deadline_at);
    if (hours !== null) {
      return (
        <span className="text-[10px] font-bold tracking-wider" style={{ color: '#F39C12' }}>
          {hours}h LEFT
        </span>
      );
    }
    return (
      <span className="text-[10px] font-bold tracking-wider" style={{ color: '#F39C12' }}>
        IN REVIEW
      </span>
    );
  }

  // paid — instructor has not accepted yet, no delivery deadline applies
  return (
    <span className="text-[10px] font-bold tracking-wider" style={{ color: '#F39C12' }}>
      PENDING
    </span>
  );
}

// ─── Left icon ────────────────────────────────────────────────────────────────

function PurchaseIcon({ status }: { status: RollReviewStatus }) {
  if (isRefundedStatus(status)) {
    return (
      <div
        className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: 'rgba(231,76,60,0.15)' }}
      >
        <RefreshCcw size={18} style={{ color: '#E74C3C' }} />
      </div>
    );
  }

  if (status === 'delivered') {
    return (
      <div
        className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: 'rgba(46,204,113,0.15)' }}
      >
        <PlayCircle size={18} style={{ color: '#2ECC71' }} />
      </div>
    );
  }

  // paid or in_review → orange clock
  return (
    <div
      className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
      style={{ backgroundColor: 'rgba(243,156,18,0.15)' }}
    >
      <Clock size={18} style={{ color: '#F39C12' }} />
    </div>
  );
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function PurchaseSkeleton() {
  return (
    <div className="flex flex-col gap-0 animate-pulse">
      {[1, 2, 3, 4].map((i) => (
        <div
          key={i}
          className="flex items-center gap-3 py-4 border-b border-white/5"
        >
          <div className="w-11 h-11 rounded-full bg-white/10 flex-shrink-0" />
          <div className="flex-1 flex flex-col gap-2">
            <div className="h-3 w-32 bg-white/10 rounded" />
            <div className="h-2.5 w-44 bg-white/10 rounded" />
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="h-3 w-14 bg-white/10 rounded" />
            <div className="h-2.5 w-16 bg-white/10 rounded" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Purchase item ────────────────────────────────────────────────────────────

function PurchaseListItem({ item }: { item: MyPurchaseItem }) {
  const dateStr = format(new Date(item.date), 'MMM d');

  return (
    <div className="flex items-center gap-3 py-4 border-b border-white/5 last:border-0">
      <PurchaseIcon status={item.status} />

      {/* Center */}
      <div className="flex-1 min-w-0">
        <p className="text-white text-sm font-bold leading-snug truncate">
          {item.instructor_name}
        </p>
        <p className="text-white/40 text-xs mt-0.5">
          {dateStr} · {item.order_ref}
        </p>
      </div>

      {/* Right */}
      <div className="flex flex-col items-end gap-1 flex-shrink-0">
        <span className="text-white text-sm font-bold">
          {formatCurrency(item.amount_cents, item.currency)}
        </span>
        <StatusLabel item={item} />
      </div>
    </div>
  );
}

// ─── MyPurchases ──────────────────────────────────────────────────────────────

const MyPurchases: React.FC = () => {
  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useMyPurchases();

  const purchases = data?.pages.flat() ?? [];

  return (
    <SubPageLayout title="My Purchases">
      <div className="flex flex-col pt-2 pb-6">
        {isLoading ? (
          <PurchaseSkeleton />
        ) : purchases.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center"
              style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}
            >
              <PlayCircle size={28} style={{ color: 'rgba(255,255,255,0.25)' }} />
            </div>
            <p className="text-white/30 text-sm text-center">
              No purchases yet.{'\n'}Browse instructors to get started.
            </p>
          </div>
        ) : (
          <>
            <div>
              {purchases.map((item) => (
                <PurchaseListItem key={item.id} item={item} />
              ))}
            </div>

            {hasNextPage && (
              <div className="flex justify-center pt-5">
                <button
                  type="button"
                  data-testid="my-purchases-load-more"
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                  className="px-5 py-2 rounded-full border border-white/15 text-white/70 text-xs font-medium hover:bg-white/5 disabled:opacity-50"
                >
                  {isFetchingNextPage ? 'Loading…' : 'See more'}
                </button>
              </div>
            )}
          </>
        )}

        {/* Footer */}
        <p className="text-center text-white/25 text-xs leading-relaxed pt-6">
          Refunds return to the original payment method in 5–10 business days.{'\n'}
          Receipts sent to your email.
        </p>
      </div>
    </SubPageLayout>
  );
};

export default MyPurchases;
